import { useState, useEffect, FormEvent } from "react";

// Models
import { AuthUser } from "../../Models/DTOs/AuthUser";
import { HelpSubject } from "../../Models/Enums/HelpSubject";

// Components
import SelectInput from "../Inputs/SelectInput/SelectInput";
import InputField from "../Inputs/InputField";
import InputTextArea from "../Inputs/InputTextArea";
import ButtonPill from "../Buttons/ButtonPill";
import SuccessMessage from "../Text/SuccessMessage";

// Hooks
import useSendSupportRequest from "../../Hooks/Brevo/useSendSupportRequest";
import ErrorMessage from "../Text/ErrorMessage";

type ContactUsProps = {
    authUser?: AuthUser | null;
};

type FormState = {
    subject: HelpSubject | null;
    email: string;
    message: string;
};

type FormErrors = {
    subject?: string;
    email?: string;
    message?: string;
};

// Local option type matching SelectInput
type SubjectOption = {
    value: HelpSubject;
    label: string;
};

const subjectOptions: SubjectOption[] = [
    { value: HelpSubject.CustomerService,   label: "Customer service / billing" },
    { value: HelpSubject.TechnicalSupport,  label: "Technical support" },
    { value: HelpSubject.ContentSuggestions,label: "Content suggestion / feedback" },
    { value: HelpSubject.Careers,           label: "Careers / working with us" },
    { value: HelpSubject.Other,             label: "Other" },
];

function ContactUs({ authUser }: ContactUsProps) {
    const
        [form, setForm] = useState<FormState>(() => ({
            subject: null,
            email: authUser?.email ?? "",
            message: "",
        })),

        [errors, setErrors] = useState<FormErrors>({}),

        { sendSupportRequest, isSending, isSuccess, error: sendError } = useSendSupportRequest(),

        handleSubjectChange = (e: { target: { name: string; value: any } }) => {
            const { name, value } = e.target;
            setForm((prev) => ({
                ...prev,
                [name]: value as HelpSubject | null,
            }));
        },

        handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const { name, value } = e.target;
            setForm((prev) => ({
                ...prev,
                [name]: value as HelpSubject | null,
            }));
        },

        handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const { name, value } = e.target;
            setForm((prev) => ({
                ...prev,
                [name]: value,
            }));
        },

        validate = (): boolean => {
            const newErrors: FormErrors = {};

            if (!form.subject) {
                newErrors.subject = "Please choose a subject.";
            }

            if (!form.email) {
                newErrors.email = "Email is required.";
            } else {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(form.email)) {
                    newErrors.email = "Please enter a valid email address.";
                }
            }

            if (!form.message || form.message.trim().length < 10) {
                newErrors.message = "Please tell us a bit more (at least 10 characters).";
            }

            setErrors(newErrors);
            return Object.keys(newErrors).length === 0;
        },

        handleSubmit = async (e: FormEvent) => {
            e.preventDefault();
            if (!validate()) return;
            if (!form.subject) return; // type guard; validate already caught this

            try {
                await sendSupportRequest({
                    subject: form.subject,
                    email: form.email,
                    message: form.message,
                    userId: authUser?.id,
                });

                // Refresh page 5 seconds after *successful* send
                setTimeout(() => {
                    window.location.reload();
                }, 10000);
            } catch {
                // error is already stored in sendError; nothing else needed here
            }
        },

        emailDisabled = !!authUser?.email;

    // Keep email in sync if authUser changes (rare, but correct)
    useEffect(() => {
        if (authUser?.email) {
            setForm((prev) => ({ ...prev, email: authUser.email! }));
        }
    }, [authUser?.email]);

    return (
        <form onSubmit={handleSubmit} className="form form--contact-us">

            {/** Subject **/}
            <SelectInput
                name="subject"
                value={form.subject}
                onChange={handleSubjectChange}
                options={subjectOptions}
                required
                hasError={!!errors.subject}
                errorMessage={errors.subject}
                placeholder="Select a subject..."
                className="contact-us__subject"
            />

            {/** Email **/}
            <InputField
                type="email"
                name="email"
                value={form.email}
                label="Email address"
                onChange={handleInputChange}
                hasError={!!errors.email}
                errorMessage={errors.email}
                required
                disabled={emailDisabled}
                className="contact-us__email"
            />

            {/** Message **/}
            <InputTextArea
                name="message"
                value={form.message}
                placeholder="How can we help?"
                onChange={handleTextAreaChange}
                hasError={!!errors.message}
                errorMessage={errors.message}
                required
                className="contact-us__message"
            />

            {isSuccess && (
                <SuccessMessage successMessage="Thank You for submitting a support ticket, we aim to respond to all requests within 24 hours. Please don't send another request unless that period has passed, rest assured your message has been received and we're working to resolve your issue in a prompt manner. The page will refresh in a few seconds." />
            )}

            {!isSuccess && sendError && (
                <ErrorMessage errorMessage={sendError} />
            )}

            <ButtonPill
                type="submit"
                className="btn btn-primary contact-us__submit"
                disabled={isSending || isSuccess}
                label={isSuccess ? "Refreshing page..." : isSending ? "Sending..." : "Send message"}
            />
        </form>
    );
}

export default ContactUs;
